import { App, ErrorService, ErrorType, PROPAGATE_HTTP_ERRORS } from '@agilox/common';
import { HttpErrorResponse, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { of, throwError } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';
import { UserStoreService } from '@user-store';
import { AuthService } from '@auth';
import * as Sentry from '@sentry/angular';
import { ErrorHandlerConfig } from './error-handler-config.interface';

/**
 * Reports server errors and request timeouts to Sentry.
 *
 * - status >= 500: passed through as the original HttpErrorResponse.
 * - status 0 while online: a request timeout / connection failure, wrapped in a
 *   synthetic Error because `sentryBeforeSend` drops every HttpErrorResponse with
 *   status 0 as offline noise — `navigator.onLine` distinguishes a real timeout
 *   from the user simply being offline.
 */
const captureHttpError = (app: App, url: string, error: HttpErrorResponse): void => {
	if (error.status >= 500) {
		Sentry.captureException(error, {
			tags: { type: 'http_server_error', app },
			extra: { url, status: error.status },
		});
		return;
	}
	if (error.status === 0 && navigator.onLine) {
		Sentry.captureException(new Error(`Http request timeout: ${url}`), {
			tags: { type: 'http_timeout', app },
			extra: { url, message: error.message },
		});
	}
};

/**
 * Shared HTTP error interceptor: reports 5xx + timeouts to Sentry, logs out on
 * auth failures, records network/offline errors, and either swallows or
 * propagates transport errors per `config.onError`.
 */
export const createErrorHandlerInterceptor =
	(config: ErrorHandlerConfig): HttpInterceptorFn =>
	(request, next) => {
		const errorService = inject(ErrorService);
		const userStore = inject(UserStoreService);
		const auth = inject(AuthService);

		const isExcluded = (url: string | null): boolean => {
			if (!url) {
				return false;
			}
			const routes = config.environment.excludedRoutes ?? [];
			const patterns = config.environment.excludedRoutePatterns ?? [];
			return (
				routes.some((route) => config.environment.server + route === url) ||
				patterns.some((pattern) => url.includes(pattern))
			);
		};

		return next(request).pipe(
			catchError((error: HttpErrorResponse) => {
				captureHttpError(config.app, request.url, error);

				const isAuthFailure = error.status === 401;
				if (isAuthFailure && userStore.currentUser()) {
					userStore.clearUser();
					auth.logout();
				}
				if (!navigator.onLine) {
					errorService.setError({
						application: config.app,
						url: request.url,
						user: userStore.currentUser(),
						type: ErrorType.NO_INTERNET_ERROR,
					});
				}

				if (config.onError === 'swallow' && !request.context.get(PROPAGATE_HTTP_ERRORS)) {
					if (isAuthFailure || isExcluded(error.url)) {
						return of(new HttpResponse({ status: error.status, body: {} }));
					}
					return of(new HttpResponse({ body: { status: false, data: [] } }));
				}
				return throwError(() => error);
			}),
			filter((event) => event instanceof HttpResponse),
			tap((response: HttpResponse<any>) => {
				if (response.status === 204 || !response.body) {
					return;
				}
				const authFailure =
					response.body.msg === 'authentication failed' ||
					response.body.error === 'user_not_loggedin';
				if (authFailure && userStore.currentUser()) {
					userStore.clearUser();
					auth.logout();
				}
			}),
			tap((response: HttpResponse<any>) => {
				if (response.status === 204 || !response.body) {
					return;
				}
				const { msg, status } = response.body;
				const authFailed = msg === 'authentication failed';
				if (status === false && !authFailed && !isExcluded(request.url)) {
					errorService.setError({
						application: config.app,
						url: request.url,
						user: userStore.currentUser(),
						type: ErrorType.NETWORK_ERROR,
						message: msg,
						body: JSON.stringify(request.body),
					});
				}
			})
		);
	};
